import React, { useEffect, useState } from 'react';

import {
  Box,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Button,
  IconButton,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';

import { useColors } from './colors';
import { cloneListingFieldsConfig } from '../utils/listingFieldsConfig.util';

const CustomItemSpecificRow = ({ item, index, onChange, onRemove }) => {
  const { borderColor, secondaryTextColor, inputColor, mutedBg } = useColors();

  return (
    <Box p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={mutedBg}>
      <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
        <FormControl flex={1} minW="200px">
          <FormLabel fontSize="xs" color={secondaryTextColor}>
            Field name
          </FormLabel>
          <Input
            size="sm"
            value={item.Name}
            onChange={(e) => onChange(index, { Name: e.target.value })}
            color={inputColor}
            fontWeight="semibold"
          />
        </FormControl>
        <HStack spacing={2}>
          <Badge colorScheme="teal" variant="outline">
            Default
          </Badge>
          <IconButton
            aria-label="Remove custom item specific"
            icon={<FaTrash />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => onRemove(index)}
          />
        </HStack>
      </HStack>

      <FormControl>
        <FormLabel fontSize="xs" color={secondaryTextColor}>
          Value
        </FormLabel>
        <Input
          size="sm"
          value={item.Value}
          onChange={(e) => onChange(index, { Value: e.target.value })}
          placeholder="1-3 business days"
          color={inputColor}
        />
      </FormControl>
    </Box>
  );
};

const ExcludedItemSpecificsList = ({ items, onChange }) => {
  const { secondaryTextColor, inputColor, borderColor, mutedBg } = useColors();
  const [newName, setNewName] = useState('');

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    const name = newName.trim();
    if (!name || items.includes(name)) return;
    onChange([...items, name]);
    setNewName('');
  };

  return (
    <VStack align="stretch" spacing={3}>
      {!items.length && (
        <Text fontSize="sm" color={secondaryTextColor}>
          No excluded fields yet. Add item specific names to strip from the reference listing.
        </Text>
      )}
      {items.map((name, index) => (
        <HStack
          key={`${name}-${index}`}
          p={3}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={mutedBg}
        >
          <Input
            size="sm"
            flex={1}
            value={name}
            onChange={(e) =>
              onChange(items.map((item, i) => (i === index ? e.target.value : item)))
            }
            placeholder="Field name to exclude"
            color={inputColor}
          />
          <IconButton
            aria-label="Remove excluded field"
            icon={<FaTrash />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => removeItem(index)}
          />
        </HStack>
      ))}
      <HStack>
        <Input
          size="sm"
          flex={1}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Field name"
          color={inputColor}
        />
        <Button
          size="sm"
          leftIcon={<FaPlus />}
          colorScheme="teal"
          variant="outline"
          onClick={addItem}
          isDisabled={!newName.trim()}
        >
          Add
        </Button>
      </HStack>
    </VStack>
  );
};

const ListingFieldsConfigSection = ({
  listingFieldsConfig,
  listingItemLocation = '',
  onChange,
  onSave,
  isSaving = false,
  embedded = false,
}) => {
  const { textColor, secondaryTextColor, inputColor } = useColors();
  const [draft, setDraft] = useState(() => cloneListingFieldsConfig(listingFieldsConfig));
  const [locationDraft, setLocationDraft] = useState(listingItemLocation);

  useEffect(() => {
    setDraft(cloneListingFieldsConfig(listingFieldsConfig));
    setLocationDraft(listingItemLocation);
  }, [listingFieldsConfig, listingItemLocation]);

  const updateDraft = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onChange?.(next);
  };

  const updateCustomSpecific = (index, patch) => {
    const customItemSpecifics = draft.customItemSpecifics.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    updateDraft({ customItemSpecifics });
  };

  const removeCustomSpecific = (index) => {
    updateDraft({
      customItemSpecifics: draft.customItemSpecifics.filter((_, i) => i !== index),
    });
  };

  const addCustomSpecific = () => {
    updateDraft({
      customItemSpecifics: [...draft.customItemSpecifics, { Name: '', Value: '' }],
    });
  };

  const handleSave = async () => {
    await onSave?.({
      listingFieldsConfig: draft,
      listingItemLocation: locationDraft,
    });
  };

  const body = (
    <>
      {!embedded && (
        <>
          <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
            Listing fields configuration
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Set initial listing values and rules applied when compiling catalog data from the
            verified reference listing.
          </Text>
        </>
      )}
      {embedded && (
        <Text color={secondaryTextColor} fontSize="sm" mb={4}>
          Configure how eBay item specifics and listing fields are built during creation. Set
          defaults, excluded fields, and core listing values.
        </Text>
      )}

      <VStack align="stretch" spacing={4} mb={6}>
        <FormControl>
          <FormLabel fontSize="sm" color={textColor}>
            Start price
          </FormLabel>
          <Input
            size="sm"
            value={draft.startPrice}
            onChange={(e) => updateDraft({ startPrice: e.target.value })}
            placeholder="99.99"
            color={inputColor}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color={textColor}>
            Quantity
          </FormLabel>
          <NumberInput
            size="sm"
            value={draft.quantity}
            min={0}
            onChange={(_, valueAsNumber) =>
              updateDraft({ quantity: Number.isFinite(valueAsNumber) ? valueAsNumber : 0 })
            }
          >
            <NumberInputField color={inputColor} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color={textColor}>
            Condition ID
          </FormLabel>
          <Input
            size="sm"
            value={draft.conditionId}
            onChange={(e) => updateDraft({ conditionId: e.target.value })}
            placeholder="1000"
            color={inputColor}
          />
          <Text fontSize="xs" color={secondaryTextColor} mt={1}>
            eBay condition ID (e.g. 1000 = New).
          </Text>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color={textColor}>
            Item location
          </FormLabel>
          <Input
            size="sm"
            value={locationDraft}
            onChange={(e) => setLocationDraft(e.target.value)}
            placeholder="e.g. Grand Prairie, TX"
            color={inputColor}
          />
          <Text fontSize="xs" color={secondaryTextColor} mt={1}>
            eBay item location applied to every listing created in a run. Save to persist across
            sessions — update before each batch if you need a different location.
          </Text>
        </FormControl>
      </VStack>

      <Accordion allowMultiple defaultIndex={[0, 1]}>
        <AccordionItem border="none" mb={3}>
          <AccordionButton
            px={4}
            py={3}
            borderWidth="1px"
            borderRadius="md"
            _expanded={{ borderBottomRadius: 0 }}
          >
            <Box flex="1" textAlign="left">
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                Custom item specifics
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                Always applied when compiling the listing (Name / Value pairs).
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel
            px={4}
            py={4}
            borderWidth="1px"
            borderTop="none"
            borderBottomRadius="md"
          >
            <VStack align="stretch" spacing={3}>
              {draft.customItemSpecifics.map((item, index) => (
                <CustomItemSpecificRow
                  key={`custom-${index}`}
                  item={item}
                  index={index}
                  onChange={updateCustomSpecific}
                  onRemove={removeCustomSpecific}
                />
              ))}
              <Button
                size="sm"
                leftIcon={<FaPlus />}
                colorScheme="teal"
                variant="outline"
                alignSelf="flex-start"
                onClick={addCustomSpecific}
              >
                Add item specific
              </Button>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
          <AccordionButton
            px={4}
            py={3}
            borderWidth="1px"
            borderRadius="md"
            _expanded={{ borderBottomRadius: 0 }}
          >
            <Box flex="1" textAlign="left">
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                Excluded item specifics
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                Removed from the reference listing during compilation.
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel
            px={4}
            py={4}
            borderWidth="1px"
            borderTop="none"
            borderBottomRadius="md"
          >
            <ExcludedItemSpecificsList
              items={draft.excludeItemSpecifics}
              onChange={(excludeItemSpecifics) => updateDraft({ excludeItemSpecifics })}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {onSave && (
        <Button
          mt={4}
          colorScheme="teal"
          size="sm"
          onClick={handleSave}
          isLoading={isSaving}
          loadingText="Saving…"
        >
          Save configuration
        </Button>
      )}
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Box
      p={5}
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="sm"
    >
      {body}
    </Box>
  );
};

export default ListingFieldsConfigSection;
